import { Injectable, inject } from '@angular/core';
import {
  LangType,
  LanguagePreferenceStorage,
  normalizeLanguagePreference,
} from '@wilson/localization';
import type { Settings } from '../interfaces/settings.interface';
import { StorageService } from './storage.service';

/**
 * Language preference storage for mobile using Capacitor Preferences (StorageService).
 * Uses a sync in-memory cache so LocalizationState.ngxsOnInit can read the value;
 * the cache must be populated in the app initializer via preload() before the store initializes.
 */
@Injectable()
export class MobileLanguagePreferenceStorage
  implements LanguagePreferenceStorage
{
  private readonly CACHE_KEY = 'wilson.language.userPreference';
  private readonly storage = inject(StorageService);

  private cache: LangType | null = null;

  getUserPreferredLanguage(): LangType | null {
    return normalizeLanguagePreference(this.cache);
  }

  setUserPreferredLanguage(lang: LangType): void {
    this.cache = lang;
    this.persist(lang);
  }

  /**
   * Call from app initializer before any code path that creates the Store.
   * Reads persisted language from storage and sets the cache so getUserPreferredLanguage() returns it when LocalizationState.ngxsOnInit runs.
   */
  async preload(): Promise<void> {
    const settings = await this.storage.persistent<Settings | null>(
      this.CACHE_KEY,
    );
    this.cache = settings?.selectedLanguage ?? null;
  }

  private persist(lang: LangType): void {
    this.storage.persistent<Settings | null>(this.CACHE_KEY).then((current) =>
      this.storage.persistent(this.CACHE_KEY, {
        ...(current ?? ({} as Settings)),
        selectedLanguage: lang,
      }),
    );
  }
}
